import { useEffect, useState } from 'react';

import type { CollectionItem, ImageCollectionItem, VideoCollectionItem } from 'components/Collection/Collection';
import { Collection } from 'components/Collection/Collection';
import SweetAlert from 'components/SweetAlert';

import { useCollectionData } from 'hooks/useCollectionData';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useAppInfo } from 'hooks/useAppInfo/useAppInfo';
import { useSettings } from 'hooks/useSettings';

import i18n from 'i18n-react';
import { Redirect } from 'react-router-dom';

interface MatchParams {
  videoId?: string;
}

export interface CollectionViewProps {
  match: { url: string; params: MatchParams };
}

export function CollectionView({ match }: CollectionViewProps) {
  const { url, params } = match;
  const { videoId } = params;
  const { salesCompanionAppId } = useSettings();
  const disabledImageShare = useFeatureFlag('disabled_image_share');
  const showImageLicenseNotice = useFeatureFlag('show_image_license_notice');
  const { data: appInfoData } = useAppInfo();
  const clientName = appInfoData?.clientName ?? '';

  const [escaped, setEscaped] = useState(false);
  const [showImageNotice, setShowImageNotice] = useState(false);

  const lastSegment = url.split('/').slice(-1).pop() ?? '';

  const { data: collection = {} } = useCollectionData(lastSegment, salesCompanionAppId);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setEscaped(true);
    };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  useEffect(() => {
    if (showImageLicenseNotice && lastSegment === 'images' && !sessionStorage.getItem('imageGalleryNoticeShown')) {
      setShowImageNotice(true);
    }
  }, [showImageLicenseNotice, lastSegment]);

  const dismissImageNotice = (): void => {
    sessionStorage.setItem('imageGalleryNoticeShown', 'true');
    setShowImageNotice(false);
  };

  const redirPath =
    videoId !== undefined ? url.substring(0, url.lastIndexOf('/')).lastIndexOf('/') : url.lastIndexOf('/');

  return (
    <div className="collection-wrapper">
      <div className="collection-wrapper__overlay" />
      <SweetAlert
        trigger={showImageNotice}
        title={i18n.translate('imageGallery.noticeTitle') as string}
        text={i18n.translate('imageGallery.noticeText', { clientName }) as string}
        confirmButtonText={i18n.translate('general.close') as string}
        onClose={dismissImageNotice}
      />

      {lastSegment === 'images' &&
        Object.values(collection).map((chapter, index) => {
          const imageItems = chapter as ImageCollectionItem[];
          const firstItem = imageItems[0];
          return (
            <Collection
              key={firstItem?.chapter.chapterNumber ?? index}
              header={firstItem?.chapter.chapterTitle}
              subheader={firstItem && <>{i18n.translate('general.chapter')} {firstItem.chapter.chapterNumber}</>}
              items={imageItems}
              disabledImageShare={disabledImageShare}
            />
          );
        })}

      {lastSegment === 'videos' &&
        Object.values(collection).map((chapterVideos, index) => {
          const videoItems = chapterVideos as VideoCollectionItem[];
          const firstItem = videoItems[0];
          return (
            <Collection
              key={index}
              header={firstItem?.chapter.chapterTitle}
              items={videoItems}
            />
          );
        })}

      {lastSegment === 'image-library' &&
        Object.values(collection).map((col, index) => (
          <div className="brochures" key={(col as CollectionItem[])[0]?.pageID ?? index}>
            <Collection
              header="Image Library"
              items={col as CollectionItem[]}
              disabledImageShare={disabledImageShare}
            />
          </div>
        ))}

      {lastSegment !== 'images' &&
        lastSegment !== 'image-library' &&
        lastSegment !== 'videos' &&
        Object.keys(collection).map((key) => (
          <div
            className={`brochures ${lastSegment}`}
            key={(collection[key] as CollectionItem[] | undefined)?.[0]?.pageID ?? key}
          >
            <Collection
              header={key}
              items={collection[key] as CollectionItem[]}
              disabledImageShare={disabledImageShare}
            />
          </div>
        ))}

      {escaped && <Redirect to={url.substring(0, redirPath)} />}
    </div>
  );
}
